import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, Routes } from '@angular/router';
import { LoginComponent } from './pages/login/login.component';
import { userRoute } from './pages/user/user.config';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { canLogin, canActivateCategories, canActivateCourses, canActivateTags, canActivateProfile, canActivateTracks, canCreateCourse, canUpdateCourse, isAuthenticated, notAuthenticated } from './shared/guard/guard';
import { PageKey } from './shared/services/page/models/page.key';
import { ProfileComponent } from './pages/profile/profile.component';
import { ForgotPwdComponent } from './pages/forgot-pwd/forgot-pwd.component';
import { ResetPwdComponent } from './pages/reset-pwd/reset-pwd.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '../environments/environment';
import { catchError, EMPTY, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from './shared/services/auth/auth.service';
import { jwtDecode } from 'jwt-decode'
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { TenantThemeService } from './shared/services/tenant-theme/tenant-theme.service';
import { DialogService } from 'primeng/dynamicdialog';
import { TrackComponent } from './pages/track/track.component';
import { CategoryComponent } from './pages/category/category.component';
import { TagComponent } from './pages/tag/tag.component';
import { CourseComponent } from './pages/course/course.component';
import { CourseListingMode } from './shared/models/enums/course-listing-mode.enum';
import { CourseCreateComponent } from './pages/course/create/course-create.component';
import { provideDashboardEcharts } from './shared/services/echarts/echarts.config';
import { ImpersonationEntryComponent } from './pages/impersonation-entry/impersonation-entry.component';
import { ensureBearerToken, stripBearerToken } from './shared/utils/bearer-token.util';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [canLogin],
    data: {
      title: 'Login',
      fullscreen: true,
      key: PageKey.LOGIN,
    },
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [isAuthenticated, canActivateProfile],
    data: {
      title: 'Perfil',
    },
  },
  {
    path: 'forgot/password',
    component: ForgotPwdComponent,
    canActivate: [notAuthenticated],
    data: {
      title: 'Esqueci Senha',
      fullscreen: true,
    },
  },
  {
    path: 'reset/password',
    component: ResetPwdComponent,
    canActivate: [notAuthenticated],
    data: {
      title: 'Nova Senha',
      fullscreen: true,
    },
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [isAuthenticated],
    data: {
      title: 'Dashboard',
      key: PageKey.DASHBOARD,
      search: false,
    },
  },
  {
    path: 'tracks',
    component: TrackComponent,
    canActivate: [isAuthenticated, canActivateTracks],
    data: {
      title: 'Tracks',
      key: PageKey.TRACKS,
      search: false,
    },
  },
  {
    path: 'courses/create',
    component: CourseCreateComponent,
    canActivate: [isAuthenticated, canCreateCourse],
    data: {
      title: 'Criar novo curso',
      key: PageKey.COURSES,
      search: false,
    },
  },
  {
    path: 'courses/drafts',
    component: CourseComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Rascunhos',
      key: PageKey.COURSE_DRAFTS,
      listingMode: CourseListingMode.DRAFTS,
      search: false,
    },
  },
  {
    path: 'courses/trash',
    component: CourseComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Cursos excluídos',
      key: PageKey.COURSE_TRASH,
      listingMode: CourseListingMode.TRASH,
      search: false,
    },
  },
  {
    path: 'courses/:courseId/edit',
    component: CourseCreateComponent,
    canActivate: [isAuthenticated, canUpdateCourse],
    data: {
      title: 'Editar curso',
      key: PageKey.COURSES,
      search: false,
    },
  },
  {
    path: 'courses',
    component: CourseComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Cursos',
      key: PageKey.COURSES,
      listingMode: CourseListingMode.PUBLISHED,
      search: false,
    },
  },
  {
    path: 'categories',
    component: CategoryComponent,
    canActivate: [isAuthenticated, canActivateCategories],
    data: {
      title: 'Categorias',
      key: PageKey.CATEGORIES,
      search: false,
    },
  },
  {
    path: 'tags',
    component: TagComponent,
    canActivate: [isAuthenticated, canActivateTags],
    data: {
      title: 'Tags',
      key: PageKey.TAGS,
      search: false,
    },
  },
  ...userRoute,
  {
    path: 'impersonate',
    component: ImpersonationEntryComponent,
    data: {
      title: 'Impersonação',
      fullscreen: true,
      impersonationEnvironment: 'tenant',
    },
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', component: NotFoundComponent, data: { title: 'Página não encontrada', fullscreen: true } },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG(),
    provideHttpClient(
      withInterceptors([baseUrlInterceptor, refreshTokenInterceptor, unauthorizedInterceptor, authInterceptor]),
    ),
    provideEnvironmentNgxMask(),
    MessageService,
    DialogService,
    provideAppInitializer(() => {
      const tenantThemeService = inject(TenantThemeService);
      // TODO: Buscar dados iniciais do tenant da API ou localStorage
      tenantThemeService.applyTenantTheme(null);
    }),
    provideDashboardEcharts(),
  ],

};

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const token = authService.getToken()

  if (token) {
    const newReq = req.clone({
      headers: req.headers.append('Authorization', ensureBearerToken(token.token)),
    });

    return next(newReq);
  }

  return next(req);
}

export function refreshTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const token = authService.getToken()

  if (!token)
    return next(req);

  const fiveSecondsInMilliseconds = 1_000 * 5;
  const maxSafeExpiresDate = +new Date() + fiveSecondsInMilliseconds;

  if (!isTokenExpired(token.token, maxSafeExpiresDate))
    return next(req);

  // Refresh token vencido: não há como renovar, então encerra a sessão e cancela a requisição
  if (!token.refreshToken || isTokenExpired(token.refreshToken, maxSafeExpiresDate)) {
    void authService.expireSession();
    return EMPTY;
  }

  // Espera o novo token antes de seguir, para a requisição não sair com o token vencido
  return from(authService.refreshToken()).pipe(
    switchMap((refreshed) => {
      if (!refreshed) {
        void authService.expireSession();
        return EMPTY;
      }

      return next(req);
    }),
  );
}

export function unauthorizedInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: unknown) => {
      // 401 com sessão salva significa token rejeitado pela API (expirado ou revogado)
      if (error instanceof HttpErrorResponse && error.status === 401 && authService.getToken())
        void authService.expireSession();

      return throwError(() => error);
    }),
  );
}

export function baseUrlInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const learningRoutes = ['/courses', '/categories', '/tags', '/tracks', '/modules', '/contents'];
  const isLearningRoute = learningRoutes.some((route) => req.url.startsWith(route));
  const baseUrl = isLearningRoute ? environment.learningApiUrl : environment.apiUrl;

  const newReq = req.clone({
    url: `${ baseUrl }${ req.url }`,
  });

  return next(newReq);
}

export function isTokenExpired(token: string, maxExpiresDate: number): boolean {
  const jwtPayload: { exp: number } = jwtDecode(stripBearerToken(token));
  return maxExpiresDate >= +new Date(jwtPayload.exp * 1000);
}
